/*
 * PES Search — contractor-grade enhancements (V12, Agent-15)
 * Presentation-only. Depends on assets/pes-search-synonyms.js (window.PesSearchSyn).
 *
 *  1. Predictive search: when the theme dropdown renders zero results, retry
 *     with synonym-expansion terms via /search/suggest.json and inject a
 *     labeled "Showing results for '<expanded>'" block into the dropdown.
 *  2. SKU-first pinning: on /search, if the query looks like a SKU/MPN, pin the
 *     exact-SKU product card above the results grid (multi-stage resolver:
 *     suggest.json -> handle slug -> /products/<handle>.js exact SKU match).
 *  3. Zero-results page: fills [data-pes-search-expansion] (rendered by
 *     snippets/pes-search-zero.liquid) with expansion results when the
 *     server-side search came back empty.
 */
(function () {
  'use strict';

  var SYN = window.PesSearchSyn;
  if (!SYN) return;

  /* ---------- shared helpers ---------- */

  function suggest(q, limit) {
    return fetch('/search/suggest.json?q=' + encodeURIComponent(q) +
                 '&resources[type]=product&resources[limit]=' + (limit || 5))
      .then(function (r) { return r.ok ? r.json() : {}; })
      .then(function (d) {
        return (d.resources && d.resources.results && d.resources.results.products) || [];
      })
      .catch(function () { return []; });
  }

  function cleanUrl(p) {
    return '/products/' + (p.handle || (p.url || '').split('/products/')[1] || '');
  }

  function money(p) {
    var v = p.price_min || p.price;
    if (v == null || v === '') return '';
    var n = parseFloat(v);
    if (isNaN(n)) return '';
    return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* PES 2026-10 truth wave: stock label derived from variant inventory data when the
     API provides it (suggest.json + products.js both expose variants[].inventory_*).
     Untracked / backorder items NEVER claim "In Stock". */
  function pesStockLabel(p) {
    var v = (p.variants && p.variants.length && p.variants[0]) || null;
    var mgmt = v ? v.inventory_management : p.inventory_management;
    var qty = v && typeof v.inventory_quantity === 'number' ? v.inventory_quantity
            : (typeof p.inventory_quantity === 'number' ? p.inventory_quantity : null);
    if (!p.available) return ['Out of Stock', 'is-out'];
    if (mgmt === 'shopify' && qty !== null && qty > 0) {
      return qty <= 10 ? ['In Stock — ' + qty + ' left', 'is-in'] : ['In Stock', 'is-in'];
    }
    return ['Available to Order', 'is-order'];
  }

  function cardHtml(p, badge) {
    var img = p.featured_image && p.featured_image.url ? p.featured_image.url : (p.image || '');
    var w = (p.featured_image && p.featured_image.width) || 300;
    var h = (p.featured_image && p.featured_image.height) || 300;
    return '' +
      '<a class="pes-sch-card" href="' + esc(cleanUrl(p)) + '">' +
        (img ? '<span class="pes-sch-card__imgwrap"><img src="' + esc(img) +
               '" width="' + w + '" height="' + h +
               '" alt="' + esc(p.title) + '" loading="lazy" decoding="async"></span>' : '') +
        '<span class="pes-sch-card__body">' +
          (badge ? '<span class="pes-sch-card__badge">' + esc(badge) + '</span>' : '') +
          '<span class="pes-sch-card__title">' + esc(p.title) + '</span>' +
          '<span class="pes-sch-card__meta">' +
            (p.vendor ? '<span class="pes-sch-card__vendor">' + esc(p.vendor) + '</span>' : '') +
            '<span class="pes-sch-card__price">' + esc(money(p)) + '</span>' +
          '</span>' +
          '<span class="pes-sch-card__stock ' + pesStockLabel(p)[1] + '">' +
            pesStockLabel(p)[0] + '</span>' +
        '</span>' +
      '</a>';
  }

  // First expansion term that yields suggest results. Calls cb(term, products)
  // or cb(null, []) when nothing expands.
  function firstExpansionHit(term, limit, cb) {
    var tries = SYN.expand(term);
    var i = 0;
    (function next() {
      if (i >= tries.length) return cb(null, []);
      var t = tries[i++];
      suggest(t, limit).then(function (prods) {
        if (prods.length) cb(t, prods); else next();
      });
    })();
  }

  /* ---------- styles ---------- */

  function injectStyles() {
    if (document.getElementById('pes-search-enhance-css')) return;
    var css = '' +
      '.pes-sch-label{display:block;width:100%;padding:.8rem 1.2rem;font-size:1.3rem;' +
        'font-weight:600;color:#0f1e2d;background:#eafaf3;border-left:3px solid #11B67B}' +
      '.pes-sch-exp-list{list-style:none;margin:0;padding:0}' +
      '.pes-sch-exp-list li{margin:0}' +
      '.pes-sch-exp-item{display:flex;gap:1rem;align-items:center;padding:.8rem 1.2rem;text-decoration:none;color:inherit}' +
      '.pes-sch-exp-item:hover{background:#f3f3f3}' +
      '.pes-sch-exp-item img{width:48px;height:48px;object-fit:contain;flex:0 0 auto}' +
      '.pes-sch-exp-item .t{font-size:1.4rem;font-weight:600;color:#0f1e2d}' +
      '.pes-sch-exp-item .p{font-size:1.3rem;color:#456}' +
      '.pes-sch-pin{grid-column:1/-1;margin:0 0 1.5rem}' +
      '.pes-sch-pin__frame{border:2px solid #11B67B;border-radius:8px;background:#fff;' +
        'box-shadow:0 4px 18px rgba(15,30,45,.10);padding:1.2rem}' +
      '.pes-sch-pin__eyebrow{display:inline-block;background:#11B67B;color:#fff;font-size:1.1rem;' +
        'font-weight:700;letter-spacing:.5px;text-transform:uppercase;padding:.3rem .9rem;' +
        'border-radius:4px;margin-bottom:.8rem}' +
      '.pes-sch-card{display:flex;gap:1.4rem;align-items:center;text-decoration:none;color:inherit}' +
      '.pes-sch-card__imgwrap{flex:0 0 auto;width:110px;height:110px;display:flex;align-items:center;justify-content:center;background:#f7f7f7;border-radius:6px}' +
      '.pes-sch-card__imgwrap img{max-width:100%;max-height:100%;object-fit:contain}' +
      '.pes-sch-card__body{display:flex;flex-direction:column;gap:.3rem}' +
      '.pes-sch-card__title{font-size:1.6rem;font-weight:700;color:#0f1e2d}' +
      '.pes-sch-card__meta{display:flex;gap:1rem;font-size:1.3rem;color:#456}' +
      '.pes-sch-card__price{font-weight:700;color:#0f1e2d}' +
      '.pes-sch-card__stock{font-size:1.2rem;font-weight:700;text-transform:uppercase}' +
      '.pes-sch-card__stock.is-in{color:#0d9a68}' +
      '.pes-sch-card__stock.is-out{color:#c0392b}' +
      '.pes-sch-card__stock.is-order{color:#64748b}' +
      '.pes-sch-strip{grid-column:1/-1;margin:0 0 2rem}' +
      '.pes-sch-strip__grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(240px,1fr));gap:1.2rem;margin-top:1.2rem}' +
      '.pes-sch-strip .pes-sch-card{flex-direction:column;align-items:stretch;border:1px solid #ddd;border-radius:8px;padding:1rem;background:#fff}' +
      '.pes-sch-strip .pes-sch-card__imgwrap{width:100%;height:140px}' +
      '.pes-sch-strip__all{display:inline-block;margin-top:1.2rem;font-weight:700;color:#0d9a68}' +
      '@media (max-width:749px){.pes-sch-card__imgwrap{width:72px;height:72px}}';
    var el = document.createElement('style');
    el.id = 'pes-search-enhance-css';
    el.textContent = css;
    document.head.appendChild(el);
  }

  /* ---------- 1. predictive dropdown expansion ---------- */

  function patchPredictive() {
    if (typeof PredictiveSearch === 'undefined' || !PredictiveSearch.prototype) return false;
    if (PredictiveSearch.prototype.__pesPatched) return true;
    PredictiveSearch.prototype.__pesPatched = true;

    var origRender = PredictiveSearch.prototype.renderSearchResults;
    PredictiveSearch.prototype.renderSearchResults = function (resultsMarkup) {
      origRender.call(this, resultsMarkup);
      try { pesMaybeExpandDropdown(this); } catch (e) { /* presentation-only */ }
    };
    return true;
  }

  function pesMaybeExpandDropdown(instance) {
    var box = instance.predictiveSearchResults;
    if (!box) return;
    // remove any stale expansion block
    var stale = box.querySelector('[data-pes-expansion]');
    if (stale) stale.remove();
    // only act on a genuine zero-results render (no list items at all)
    if (box.querySelector('li, .predictive-search__list-item')) return;
    var term = (instance.getQuery ? instance.getQuery() : '').trim();
    if (term.length < 2) return;

    firstExpansionHit(term, 5, function (used, prods) {
      if (!used || !prods.length) return;
      // dropdown may have re-rendered since; only inject if still empty for same term
      if (!instance.isOpen && !instance.getAttribute('open')) return;
      if ((instance.getQuery ? instance.getQuery() : '').trim() !== term) return;
      if (box.querySelector('li, .predictive-search__list-item')) return;

      var wrap = document.createElement('div');
      wrap.setAttribute('data-pes-expansion', '');
      var html = '<span class="pes-sch-label">No matches for &ldquo;' + esc(term) +
                 '&rdquo; — showing results for &ldquo;' + esc(used) + '&rdquo;</span>' +
                 '<ul class="pes-sch-exp-list">';
      prods.forEach(function (p) {
        var img = p.featured_image && p.featured_image.url ? p.featured_image.url : (p.image || '');
        html += '<li><a class="pes-sch-exp-item" href="' + esc(cleanUrl(p)) + '">' +
                (img ? '<img src="' + esc(img) + '" width="48" height="48" alt="" loading="lazy" decoding="async">' : '') +
                '<span><span class="t">' + esc(p.title) + '</span><br>' +
                '<span class="p">' + esc(money(p)) + '</span></span></a></li>';
      });
      html += '</ul>';
      wrap.innerHTML = html;
      box.appendChild(wrap);
      // keep dropdown sized correctly after injection
      if (instance.open) instance.open();
    });
  }

  /* ---------- 2. SKU-first pinning on /search ---------- */

  function skuResolve(term, cb) {
    // Stage 1: suggest.json candidates
    suggest(term, 6).then(function (prods) {
      var handles = prods.map(function (p) { return p.handle; }).filter(Boolean);
      // Stage 3-style: term may itself be a handle
      var slug = term.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
      if (slug.length > 2 && handles.indexOf(slug) === -1) handles.push(slug);
      var q = term.toLowerCase().replace(/[^a-z0-9]/g, '');
      var i = 0;
      var partial = null; // best prefix/substring SKU match while scanning
      (function next() {
        if (i >= handles.length || i >= 4) return cb(partial);
        var h = handles[i++];
        fetch('/products/' + h + '.js')
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (prod) {
            if (!prod || !prod.variants) return next();
            var exact = prod.variants.filter(function (v) {
              var s = (v.sku || '').toLowerCase().replace(/[^a-z0-9]/g, '');
              return s && s === q;
            })[0];
            if (exact) return cb({ prod: prod, variant: exact, exact: true });
            if (!partial && q.length >= 3) {
              var near = prod.variants.filter(function (v) {
                var s = (v.sku || '').toLowerCase().replace(/[^a-z0-9]/g, '');
                return s && (s.indexOf(q) === 0 || s.indexOf(q + '') !== -1);
              })[0];
              if (near) partial = { prod: prod, variant: near, exact: false };
            }
            next();
          })
          .catch(next);
      })();
    });
  }

  function pinSkuCard() {
    var grid = document.querySelector('#product-grid ul.product-grid, #product-grid');
    if (!grid) return;
    var termEl = document.getElementById('Search-In-Template');
    var term = (termEl && termEl.value ? termEl.value : new URLSearchParams(location.search).get('q') || '').trim();
    if (!SYN.isSkuLike(term)) return;

    skuResolve(term, function (hit) {
      if (!hit) return;
      if (document.querySelector('.pes-sch-pin')) return;
      // skip if the resolved product is already the first result
      var first = grid.querySelector('a[href*="/products/"]');
      if (first && (first.getAttribute('href') || '').indexOf('/products/' + hit.prod.handle) === 0) return;
      var v = hit.variant, prod = hit.prod;
      var img = (v.featured_image && v.featured_image.src) || prod.featured_image || '';
      var pinned = {
        title: prod.title + (prod.variants.length > 1 ? ' — ' + v.title : ''),
        handle: prod.handle,
        vendor: prod.vendor,
        price: (v.price / 100).toFixed(2),
        available: v.available,
        variants: [v],
        featured_image: img ? { url: img, width: 300, height: 300 } : null
      };
      var li = document.createElement('li');
      li.className = 'pes-sch-pin';
      li.innerHTML = '<div class="pes-sch-pin__frame">' +
        '<span class="pes-sch-pin__eyebrow">' + (hit.exact ? 'Exact SKU match · ' : 'Closest SKU match · ') + esc(v.sku) + '</span>' +
        cardHtml(pinned, null) + '</div>';
      grid.insertBefore(li, grid.firstChild);
    });
  }

  /* ---------- 3. zero-results expansion strip ---------- */

  function fillZeroStrip() {
    var slot = document.querySelector('[data-pes-search-expansion]');
    if (!slot) return;
    var term = (document.querySelector('.template-search--empty #Search-In-Template') || {}).value ||
               new URLSearchParams(location.search).get('q') || '';
    term = term.trim();
    if (term.length < 2) return;

    firstExpansionHit(term, 8, function (used, prods) {
      if (!used || !prods.length) return;
      var html = '<div class="pes-sch-strip__inner">' +
        '<span class="pes-sch-label">No matches for &ldquo;' + esc(term) +
        '&rdquo; — showing results for &ldquo;' + esc(used) + '&rdquo;</span>' +
        '<div class="pes-sch-strip__grid">';
      prods.slice(0, 4).forEach(function (p) { html += cardHtml(p, null); });
      html += '</div><a class="pes-sch-strip__all" href="/search?q=' +
              encodeURIComponent(used) + '&options%5Bprefix%5D=last">View all results for &ldquo;' +
              esc(used) + '&rdquo; &rarr;</a></div>';
      slot.innerHTML = html;
      slot.hidden = false;
    });
  }

  /* ---------- 0. search-header flex cleanup ----------
     pes-collection-ui-v6.js (PLP scope, shared file) force-flexes the FIRST
     .cstm-collect-facets-container on the page — on /search that is the
     search header, squishing H1/search-bar/zero block into columns.
     Counter it here (presentation-only, search scope). */
  function fixSearchHeaderFlex() {
    var h = document.querySelector('.template-search__header.cstm-collect-facets-container');
    if (!h) return;
    function clean() {
      if (h.style.display === 'flex') {
        h.style.removeProperty('display');
        h.style.removeProperty('gap');
        h.style.removeProperty('align-items');
      }
    }
    clean();
    var mo = new MutationObserver(clean);
    mo.observe(h, { attributes: true, attributeFilter: ['style'] });
    setTimeout(function () { mo.disconnect(); }, 10000);
  }

  /* ---------- boot ---------- */

  function boot() {
    injectStyles();
    fixSearchHeaderFlex();
    // PredictiveSearch may register after us (both defer) — retry briefly.
    var tries = 0;
    (function ensure() {
      if (patchPredictive() || ++tries > 20) return;
      setTimeout(ensure, 250);
    })();
    if (location.pathname.indexOf('/search') === 0) {
      pinSkuCard();
      fillZeroStrip();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
